/**
 * Grafana embedding utilities
 *
 * MULTI-TENANT: Dashboard URLs are now generated via signed URL API
 * to ensure tenant isolation. The API injects tenant_id from the
 * authenticated user's JWT, preventing client-side tampering.
 */
import { apiClient } from '../api/client';

// Dashboard configuration
export const GRAFANA_URL = 'https://grafana.oddduckfarm.com';

export const DASHBOARD_UIDS = {
  'portfolio-overview': '7e590306-640d-4691-b32f-564363b0a980',
  'price-history': '247ea147-3684-4479-82ad-a31a51365aa0',
  'performance': 'performance-dashboard',
  'risk-volatility': 'risk-volatility-dashboard',
  'watchlist': 'watchlist-dashboard',
} as const;

export type DashboardType = keyof typeof DASHBOARD_UIDS;

export interface GrafanaEmbedParams {
  /** Portfolio ID to filter by */
  portfolio?: string;
  /** Tag IDs to filter by (comma-separated) */
  tags?: string;
  /** Symbol to filter by */
  symbol?: string;
  /** Time range start (e.g., 'now-7d') */
  from?: string;
  /** Time range end (e.g., 'now') */
  to?: string;
  /** Theme (light or dark) */
  theme?: 'light' | 'dark';
  /** Refresh interval (e.g., '5s', '1m', '5m') */
  refresh?: string;
}

export interface SignedUrlResponse {
  url: string;
  expires_at: number;
}

/**
 * Fetch a signed Grafana dashboard URL from the API.
 *
 * The API injects tenant_id from the authenticated user's JWT,
 * signs the URL with HMAC, and returns a time-limited URL.
 */
export async function getSignedDashboardUrl(
  dashboard: DashboardType,
  params: GrafanaEmbedParams = {}
): Promise<SignedUrlResponse> {
  const uid = DASHBOARD_UIDS[dashboard];

  // Build query params for the API
  const queryParams = new URLSearchParams();

  if (params.portfolio) {
    queryParams.set('var-portfolio', params.portfolio);
  }
  if (params.tags) {
    queryParams.set('var-tags', params.tags);
  }
  if (params.symbol) {
    queryParams.set('var-symbol', params.symbol);
  }
  if (params.from) {
    queryParams.set('from', params.from);
  }
  if (params.to) {
    queryParams.set('to', params.to);
  }
  if (params.theme) {
    queryParams.set('theme', params.theme);
  }
  if (params.refresh) {
    queryParams.set('refresh', params.refresh);
  }

  const queryString = queryParams.toString();
  const apiUrl = `/api/grafana/signed-url/${uid}${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get<SignedUrlResponse>(apiUrl);
  return response.data;
}

/**
 * Build a Grafana embed URL for a full dashboard (LEGACY - sync version).
 *
 * NOTE: This function does NOT include tenant isolation. It's kept for
 * backwards compatibility and admin use. For multi-tenant embedding,
 * use getSignedDashboardUrl() instead.
 */
export function getGrafanaDashboardUrl(
  dashboard: DashboardType,
  params: GrafanaEmbedParams = {}
): string {
  const uid = DASHBOARD_UIDS[dashboard];
  const url = new URL(`${GRAFANA_URL}/d/${uid}`);

  // Add kiosk mode for clean embedding (no sidebar, no header)
  url.searchParams.set('kiosk', '1');

  // Add theme
  if (params.theme) {
    url.searchParams.set('theme', params.theme);
  }

  // Add time range
  if (params.from) {
    url.searchParams.set('from', params.from);
  }
  if (params.to) {
    url.searchParams.set('to', params.to);
  }

  // Add refresh interval
  if (params.refresh) {
    url.searchParams.set('refresh', params.refresh);
  }

  // Add filter variables
  if (params.portfolio) {
    url.searchParams.set('var-portfolio', params.portfolio);
  }
  if (params.tags) {
    url.searchParams.set('var-tags', params.tags);
  }
  if (params.symbol) {
    url.searchParams.set('var-symbol', params.symbol);
  }

  return url.toString();
}

/**
 * Build a Grafana embed URL for a single panel (LEGACY - sync version).
 *
 * NOTE: This function does NOT include tenant isolation.
 */
export function getGrafanaPanelUrl(
  dashboard: DashboardType,
  panelId: number,
  params: GrafanaEmbedParams = {}
): string {
  const uid = DASHBOARD_UIDS[dashboard];
  const url = new URL(`${GRAFANA_URL}/d-solo/${uid}`);

  // Add panel ID
  url.searchParams.set('panelId', panelId.toString());

  // Add theme
  if (params.theme) {
    url.searchParams.set('theme', params.theme);
  }

  // Add time range
  if (params.from) {
    url.searchParams.set('from', params.from);
  }
  if (params.to) {
    url.searchParams.set('to', params.to);
  }

  // Add filter variables
  if (params.portfolio) {
    url.searchParams.set('var-portfolio', params.portfolio);
  }
  if (params.tags) {
    url.searchParams.set('var-tags', params.tags);
  }
  if (params.symbol) {
    url.searchParams.set('var-symbol', params.symbol);
  }

  return url.toString();
}

/**
 * Detect system theme preference
 */
export function getSystemTheme(): 'light' | 'dark' {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/**
 * Calculate when to refresh the signed URL (before it expires).
 * Returns milliseconds until refresh needed.
 */
export function getRefreshDelay(expiresAt: number): number {
  const now = Math.floor(Date.now() / 1000);
  const expiresIn = expiresAt - now;

  // Refresh 60 seconds before expiry, or immediately if almost expired
  const refreshIn = Math.max(0, expiresIn - 60);

  return refreshIn * 1000; // Convert to milliseconds
}
